import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { employeeApi, type EmployeeWeeklyReport } from '../api/client';
import {
  formatWeekRange,
  isLastWeekReport,
  localToday,
  showRecordingDays,
  visibleWeeklyReports,
} from '../utils/weeklyReport';

/**
 * ダッシュボードの「先週の振り返り」（課題 #59 段階 4）。
 * 前の週の週次レポートがあるときだけ出す。取得に失敗したときも、ダッシュボードの
 * ほかの表示を妨げないよう何も出さない（振り返り履歴の画面ではエラーを出す）。
 */
export default function LastWeekReportCard() {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const [report, setReport] = useState<EmployeeWeeklyReport | null>(null);

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    let cancelled = false;
    employeeApi
      .getWeeklyReports(token, { limit: 1 })
      .then((res) => {
        if (cancelled) return;
        const latest = visibleWeeklyReports(res.data ?? [])[0];
        setReport(latest && isLastWeekReport(latest, localToday()) ? latest : null);
      })
      .catch(() => {
        if (!cancelled) setReport(null);
      });
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  if (!report) return null;

  return (
    <section
      className="dashboard-section dashboard-weekly"
      aria-labelledby="dashboard-weekly-heading"
      data-testid="last-week-report"
    >
      <h2 id="dashboard-weekly-heading">
        {t('weeklyReport.lastWeekTitle', { range: formatWeekRange(report.week_start, report.week_end) })}
      </h2>
      <p className="weekly-report-comment">{report.comment}</p>
      {showRecordingDays(report.recording_days) && (
        <p className="weekly-report-days">{t('weeklyReport.recordingDays', { count: report.recording_days })}</p>
      )}
      <p className="weekly-report-more">
        <Link to="/dashboard/history">{t('weeklyReport.seeHistory')}</Link>
      </p>
    </section>
  );
}
